import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, PaginationData } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';

interface SupplierOrder {
  id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_code: string;
  order_id: string;
  order_number: string;
  status: string;
  total_cost: number | null;
  items_count: number;
  tracking_number: string | null;
  tracking_carrier: string | null;
  pipeline_source: string | null;
  forward_error: string | null;
  created_at: string;
  sent_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
}

interface SupplierOrdersResponse {
  orders: SupplierOrder[];
  pagination: PaginationData;
}

type StatusFilter = 'all' | 'pending' | 'sent' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';

const statusBadges: Record<string, { variant: any; label: string }> = {
  pending: { variant: 'yellow', label: 'Pending' },
  sent: { variant: 'blue', label: 'Sent' },
  confirmed: { variant: 'blue', label: 'Confirmed' },
  shipped: { variant: 'green', label: 'Shipped' },
  delivered: { variant: 'green', label: 'Delivered' },
  cancelled: { variant: 'red', label: 'Cancelled' },
};

const columns: ColumnDef<SupplierOrder>[] = [
  {
    id: 'supplier',
    header: 'Supplier',
    cell: ({ row }) => (
      <div>
        <Link to={`/admin/suppliers/${row.supplier_id}`} className="font-medium text-sm text-blue-600 hover:text-blue-800">
          {row.supplier_name}
        </Link>
        <div className="text-xs text-gray-500">{row.supplier_code}</div>
      </div>
    ),
  },
  {
    id: 'order',
    header: 'Customer Order',
    cell: ({ row }) => (
      <Link to={`/admin/orders/${row.order_id}`} className="text-sm text-blue-600 hover:text-blue-800">
        #{row.order_number}
      </Link>
    ),
  },
  {
    id: 'items',
    header: 'Items',
    accessorKey: 'items_count',
    align: 'right',
    cell: ({ row }) => <span className="text-sm">{row.items_count}</span>,
  },
  {
    id: 'cost',
    header: 'Cost',
    accessorKey: 'total_cost',
    align: 'right',
    cell: ({ row }) => (
      <span className="text-sm font-medium">
        {row.total_cost ? formatCurrency(row.total_cost) : '-'}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const badge = statusBadges[row.status] || { variant: 'gray', label: row.status };
      return <Badge variant={badge.variant}>{badge.label}</Badge>;
    },
  },
  {
    id: 'source',
    header: 'Source',
    cell: ({ row }) => {
      const source = row.pipeline_source || 'manual';
      const sourceColors: Record<string, string> = {
        pipeline: 'bg-blue-100 text-blue-700',
        agent: 'bg-purple-100 text-purple-700',
        manual: 'bg-stone-100 text-stone-600',
      };
      return (
        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${sourceColors[source] || sourceColors.manual}`}>
          {source}
        </span>
      );
    },
  },
  {
    id: 'tracking',
    header: 'Tracking',
    cell: ({ row }) =>
      row.tracking_number ? (
        <div className="text-sm">
          <span className="text-gray-500">{row.tracking_carrier}:</span> {row.tracking_number}
        </div>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    sortable: true,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">
        {new Date(row.created_at).toLocaleDateString()}
      </span>
    ),
  },
];

const SupplierOrders = () => {
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { state, updateState } = useDataTableState({
    storageKey: 'admin-supplier-orders',
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultLimit: 25,
    columns,
    filterKeys: ['status'],
  });

  const statusFilter = (state.filters.status || 'all') as StatusFilter;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (statusFilter !== 'all') params.set('status', statusFilter);
        const response = await api.get<SupplierOrdersResponse>(`/admin/supplier-orders?${params.toString()}`);
        setOrders(response.orders);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load supplier orders');
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, [state.page, state.limit, statusFilter, state.sort, state.order]);

  const handleStatusFilter = (status: StatusFilter) => {
    updateState({ filters: { ...state.filters, status: status === 'all' ? '' : status } });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Supplier Orders</h1>
        <p className="text-gray-600 mt-1">Track orders forwarded to dropshipping suppliers</p>
      </div>

      <Card padding="md">
        <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
          {(['all', 'pending', 'sent', 'confirmed', 'shipped', 'delivered', 'cancelled'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'px-3 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap',
                statusFilter === status
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              )}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={orders}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        searchPlaceholder="Search supplier orders..."
        emptyState={
          <div className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No supplier orders</h3>
            <p className="text-gray-600">
              Supplier orders are created when customers purchase dropshipped products.
            </p>
          </div>
        }
      />
    </div>
  );
};

export default SupplierOrders;
